const ABORT_ERROR_MESSAGES = [
  "signal is aborted without reason",
  "the operation was aborted",
  "the user aborted a request",
  "fetch is aborted",
];

/**
 * Detects benign AbortErrors thrown by Next.js's internal
 * `fetch-server-response` during client-side RSC navigation.
 *
 * When a user navigates away before an RSC payload fetch completes,
 * the AbortController cancels the in-flight request and produces an
 * AbortError with "signal is aborted without reason". These are not
 * actionable application errors.
 *
 * In production, the stack trace is minified and won't contain the
 * original "fetch-server-response.ts" path, so we match on:
 * 1. The error kind/type being "AbortError", OR the stack starting
 *    with "AbortError:" (for logs where kind is unavailable)
 * 2. The message OR stack containing a known abort-specific string
 *    (RUM network errors wrap the original message as
 *    "Fetch error GET <url>", so the abort reason only appears in
 *    the stack)
 */
export function isFetchServerResponseAbortError(error: {
  message?: string;
  stack?: string;
  kind?: string;
}): boolean {
  const normalizedMessage = error.message?.toLowerCase().trim() ?? "";
  const normalizedStack = error.stack?.toLowerCase() ?? "";

  const isAbortKind = error.kind === "AbortError";
  const isAbortStack = normalizedStack.startsWith("aborterror:");

  if (!isAbortKind && !isAbortStack) {
    return false;
  }

  return ABORT_ERROR_MESSAGES.some(
    (msg) => normalizedMessage.includes(msg) || normalizedStack.includes(msg),
  );
}
